// ============================================================================
// ReportSection — the shared frame every report section sits in.
// ----------------------------------------------------------------------------
// A Pulse Card with a kicker + title + subtitle header, a scroll-into-view
// rise animation, and a graceful "not enough data yet" fallback when the
// section has no data (so each section renders ONLY if it has data — the
// optional-first contract). Reuses the locked design system Card; does not
// restyle it.
// ============================================================================
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components";

export interface ReportSectionProps {
  /** Section index, used to stagger the rise-in animation. */
  index?: number;
  kicker?: ReactNode;
  title: string;
  subtitle?: string;
  /** When false, the section renders its empty marker instead of children. */
  hasData: boolean;
  /** Optional action rendered on the right of the header (e.g. a toggle). */
  action?: ReactNode;
  children: ReactNode;
  /** Anchor id so the section is linkable / printable. */
  id?: string;
}

/** One titled report section with a graceful empty state. */
export function ReportSection({
  index = 0,
  kicker,
  title,
  subtitle,
  hasData,
  action,
  children,
  id,
}: ReportSectionProps) {
  const { t } = useTranslation("clientReport");
  const reduce = useReducedMotion();

  return (
    <motion.section
      id={id}
      initial={reduce ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.5,
        delay: reduce ? 0 : Math.min(index * 0.05, 0.25),
        ease: [0.22, 0.61, 0.36, 1],
      }}
    >
      <Card className="break-inside-avoid">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {kicker && (
              <p className="mb-1 text-xs font-bold uppercase tracking-kicker text-coral">
                {kicker}
              </p>
            )}
            <h2 className="font-display text-xl font-extrabold tracking-tightest text-body sm:text-2xl">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1 text-sm text-body-soft">{subtitle}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>

        {hasData ? (
          children
        ) : (
          <div className="rounded-lg bg-surface-sunken/60 px-6 py-10 text-center ring-1 ring-line-soft">
            <p className="font-display text-base font-bold text-body-soft">
              {t("empty.section")}
            </p>
            <p className="mt-1 text-sm text-body-faint">
              {t("empty.sectionHint")}
            </p>
          </div>
        )}
      </Card>
    </motion.section>
  );
}
